import { ReactNode } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface KpiCardProps {
  label: string
  value: ReactNode
  icon?: ReactNode
  /** A text-color utility class, e.g. 'text-emerald-500'. Applied to the value text and the icon box — pass a text color only, NOT a background class. */
  accent?: string
  /** Unit rendered muted right after the value, e.g. "d", "/ mo", "months". */
  suffix?: ReactNode
  /** Trend chip next to the label: an up/down arrow + value, toned emerald
   *  (positive) or destructive (negative). */
  delta?: { value: ReactNode; positive?: boolean }
  hint?: ReactNode
  className?: string
}

export function KpiCard({ label, value, icon, accent = 'text-foreground', suffix, delta, hint, className }: KpiCardProps) {
  return (
    <div className={cn('flex items-center gap-3 rounded-xl bg-card p-4 ring-1 ring-border', className)}>
      {icon && <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg bg-muted/50', accent)}>{icon}</div>}
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <div className={cn('text-2xl font-semibold tabular-nums', accent)}>{value}</div>
          {suffix != null && <div className="text-xs text-muted-foreground">{suffix}</div>}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
          {delta && (
            <span className={cn('inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums', delta.positive ? 'text-emerald-500' : 'text-destructive')}>
              {delta.positive ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
              {delta.value}
            </span>
          )}
        </div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </div>
    </div>
  )
}

export function KpiRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-2 gap-4 lg:grid-cols-4', className)}>{children}</div>
}
